import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cx } from "@/lib/cx";

/**
 * Quiet, illustration-free empty state that sits in the content column (left aligned under the
 * page header), not floating mid-page. One title, one sentence, then actions.
 *
 *   <EmptyState
 *     icon={Tray}
 *     title="Your inbox is clear"
 *     description="One sentence on what will appear here."
 *     action={<Button variant="primary">Create item <Kbd keys={["C"]} tone="inverse" /></Button>}
 *     secondary={<Button variant="secondary">Import</Button>}
 *     hint={<KeyHint keys={["G", "M"]}>My work</KeyHint>}
 *   />
 *
 * One primary per view: when the page header already carries the primary "New ..." button, the
 * empty state repeats it as `secondary`, so the eye still lands on one dark button.
 *
 * `align="center"` is for whole-page states (404, permission denied) with no page header above.
 * `headingLevel` keeps the outline correct: the page title is the h1, so the default is h2.
 */
export function EmptyState({
  icon: I,
  title,
  description,
  action,
  secondary,
  hint,
  align = "start",
  headingLevel = 2,
  className,
}: {
  icon?: Icon;
  title: string;
  description: string;
  action?: ReactNode;
  secondary?: ReactNode;
  /** Keyboard hints, shown after the actions in a quieter tone. */
  hint?: ReactNode;
  align?: "start" | "center";
  headingLevel?: 2 | 3;
  className?: string;
}) {
  const H = headingLevel === 2 ? "h2" : "h3";
  const center = align === "center";
  return (
    <div
      className={cx(
        "flex max-w-md flex-col",
        center ? "mx-auto items-center px-6 py-16 text-center" : "items-start",
        className,
      )}
    >
      {I ? (
        <span className="mb-4 flex size-8 items-center justify-center rounded-control border border-line bg-surface text-fg-muted shadow-[0_1px_2px_var(--shadow-soft)]">
          <I size={16} aria-hidden="true" />
        </span>
      ) : null}
      <H className="text-base font-semibold tracking-snug text-fg">{title}</H>
      <p className="mt-1 text-sm text-fg-muted text-pretty">{description}</p>
      {action || secondary ? (
        <div className={cx("mt-5 flex flex-wrap items-center gap-2", center && "justify-center")}>
          {action}
          {secondary}
        </div>
      ) : null}
      {hint ? (
        <div
          className={cx(
            "mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-fg-subtle",
            center && "justify-center",
          )}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}
